import { Head, Link, router } from '@inertiajs/react'
import Layout from '../../components/Layout'

interface AgendaTemplateSummary {
  id: number
  name: string
  description: string | null
  meeting_type: string
  section_count: number
  total_duration_minutes: number
  team_count: number
  team_names: string[]
  is_required: boolean
  required_kind: string | null
  can_delete: boolean
}

interface Props {
  templates: AgendaTemplateSummary[]
  can_create: boolean
}

const MEETING_TYPE_LABELS: Record<string, string> = {
  weekly_tactical: 'Weekly Tactical',
  monthly_strategic: 'Monthly Strategic',
  monthly_financial: 'Monthly Financial',
  rock_review: 'Rock Review',
  monthly_financial_and_rock_review: 'Financial Rock & Accountability',
  annual_planning: 'Annual Planning',
  custom: 'Custom',
}

function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m} min`
}

export default function AgendaTemplatesIndex({ templates, can_create }: Props) {
  return (
    <Layout>
      <Head title="Agenda Templates — Pinnacle" />

      <div className="max-w-screen-lg mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-heading">Agenda Templates</h1>
            <p className="text-sm text-text-secondary mt-1">
              {templates.length} template{templates.length !== 1 ? 's' : ''} across your organization
            </p>
          </div>
          {can_create && (
            <Link
              href="/agenda_templates/new"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New Template
            </Link>
          )}
        </div>

        {templates.length === 0 ? (
          <div className="bg-white rounded-lg border border-stone p-16 text-center">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-text-primary/20 mb-4">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            <h3 className="font-semibold text-text-primary mb-1">No agenda templates yet</h3>
            <p className="text-sm text-text-secondary mb-4">
              Create reusable meeting agendas for your teams.
            </p>
            {can_create && (
              <Link
                href="/agenda_templates/new"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors"
              >
                Create First Template
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map(template => (
              <div
                key={template.id}
                className="bg-white rounded-lg border border-stone p-5 flex items-center gap-4 hover:shadow-sm transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link
                      href={`/agenda_templates/${template.id}`}
                      className="font-semibold text-text-primary hover:underline transition-colors"
                    >
                      {template.name}
                    </Link>
                    <span className="text-sm text-text-muted bg-stone px-2 py-0.5 rounded-full">
                      {MEETING_TYPE_LABELS[template.meeting_type] || template.meeting_type}
                    </span>
                    {template.is_required && (
                      <span
                        className="text-xs font-semibold uppercase tracking-wide text-text-primary bg-marigold/30 border border-marigold/50 px-2 py-0.5 rounded-full"
                        title="This template is required and cannot be deleted"
                      >
                        Required
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-text-secondary truncate">{template.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-sm text-text-muted">
                    <span>{template.section_count} section{template.section_count !== 1 ? 's' : ''}</span>
                    <span>·</span>
                    <span>{formatDuration(template.total_duration_minutes)}</span>
                    {template.team_count > 0 ? (
                      <>
                        <span>·</span>
                        <span title={template.team_names.join(', ')}>
                          {template.team_count} team{template.team_count !== 1 ? 's' : ''}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-status-at-risk bg-status-at-risk-bg border border-status-at-risk/30 px-2 py-0.5 rounded-full">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                          Not assigned to any team
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {template.team_names.slice(0, 3).map(name => (
                    <span
                      key={name}
                      className="hidden md:inline-block text-sm font-medium text-text-primary bg-charcoal/5 px-2 py-0.5 rounded-full"
                    >
                      {name}
                    </span>
                  ))}
                  {template.team_names.length > 3 && (
                    <span className="hidden md:inline-block text-sm text-text-muted">
                      +{template.team_names.length - 3} more
                    </span>
                  )}
                  <Link
                    href={`/agenda_templates/${template.id}`}
                    className="ml-2 opacity-40 hover:opacity-70 transition-opacity"
                    aria-label="Edit template"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </Link>
                  {template.can_delete && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Delete the "${template.name}" agenda template? This cannot be undone.`)) {
                          router.delete(`/agenda_templates/${template.id}`, { preserveScroll: true })
                        }
                      }}
                      className="opacity-40 hover:opacity-100 hover:text-pinnacle-red transition-opacity"
                      aria-label="Delete template"
                      title="Delete template"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/>
                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
